import React, { useCallback, useState } from 'react'

import { HiChevronUpDown } from 'react-icons/hi2'
import { twMerge } from 'tailwind-merge'

import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxList,
  ComboboxSelectableItem,
  ComboboxTrigger,
} from '@/components/common/ComboboxV2'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/common/Select'
import { TEXT_INPUT_CLASSNAME } from '@/components/common/TextInputV2'
import { getCompanyName } from '@/components/library-v2/companies/lib'
import { CompanyAvatar } from '@/components/library-v2/companies/Table/cells'

import type { UUID } from '@/types/Id'
import type { EntityCompany } from '@/types/library/Entity'

interface CompanyComboboxProps {
  value: UUID | null
  onChange: (companyId: UUID) => void
  onClear?: () => void
  companies?: EntityCompany[]
  placeholder?: string
  disabled?: boolean
}

export const CompanyCombobox: React.FC<CompanyComboboxProps> = ({
  value,
  onChange,
  onClear,
  companies,
  placeholder = 'Select company...',
  disabled = false,
}) => {
  const [open, setOpen] = useState<boolean>(false)

  const handleSelect = useCallback(
    (companyId: UUID) => () => {
      onChange(companyId)
      setOpen(false)
    },
    [onChange, setOpen],
  )

  const handleClear = useCallback(() => {
    onClear?.()
    setOpen(false)
  }, [onClear, setOpen])

  const selectedCompany = companies?.find((c) => c.id === value)

  return (
    <Combobox open={open} onOpenChange={setOpen}>
      <ComboboxTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={twMerge(
            TEXT_INPUT_CLASSNAME,
            'w-full flex items-center justify-between px-3 h-9 text-sm bg-primary-50 dark:bg-default-dark border border-primary-200 dark:border-primary-800 rounded-md hover:bg-primary-100 dark:hover:bg-primary-900 disabled:opacity-50 disabled:cursor-not-allowed',
          )}
        >
          {selectedCompany ? (
            <CompanyItem company={selectedCompany} />
          ) : (
            <span className="text-sm truncate text-primary-500 dark:text-primary-400">
              {placeholder}
            </span>
          )}
          <HiChevronUpDown size={16} className="flex-shrink-0" />
        </button>
      </ComboboxTrigger>
      <ComboboxContent align="start" className="p-0 w-[320px]">
        <ComboboxInput
          placeholder="Search companies..."
          className="placeholder:text-sm"
        />
        <ComboboxList>
          <ComboboxEmpty className="text-sm">No companies found.</ComboboxEmpty>
          <ComboboxGroup>
            {value && onClear ? (
              <ComboboxSelectableItem
                key="__clear__"
                value="__clear__"
                isSelected={false}
                onSelect={handleClear}
              >
                <span className="text-sm text-red-600 dark:text-red-400">
                  Clear selection
                </span>
              </ComboboxSelectableItem>
            ) : null}
            {(companies ?? []).map((company) => {
              return (
                <ComboboxSelectableItem
                  key={company.id}
                  value={`${company.name}-${company.id}`}
                  isSelected={value === company.id}
                  onSelect={handleSelect(company.id)}
                >
                  <div className="flex items-center gap-x-2 truncate">
                    <CompanyAvatar company={company} width={20} height={20} />
                    <span className="text-sm truncate">
                      {getCompanyName(company)}
                    </span>
                  </div>
                </ComboboxSelectableItem>
              )
            })}
          </ComboboxGroup>
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  )
}

interface CompanyItemProps {
  company: EntityCompany
}

const CompanyItem: React.FC<CompanyItemProps> = ({ company }) => {
  return (
    <div className="flex items-center gap-x-2 truncate">
      <CompanyAvatar company={company} width={20} height={20} />
      <span className="text-sm truncate">{getCompanyName(company)}</span>
    </div>
  )
}

export const CompanySelect: React.FC<CompanyComboboxProps> = ({
  value,
  onChange,
  companies,
  placeholder = 'Select company...',
  disabled = false,
}) => {
  const handleValueChange = useCallback(
    (companyId: string) => {
      onChange(companyId as UUID)
    },
    [onChange],
  )

  const selectedCompany = companies?.find((c) => c.id === value)

  return (
    <Select
      value={value || ''}
      onValueChange={handleValueChange}
      disabled={disabled}
    >
      <SelectTrigger className="w-full dark:bg-default-dark">
        <SelectValue placeholder={placeholder}>
          {selectedCompany ? (
            <CompanyItem company={selectedCompany} />
          ) : (
            <span className="text-sm truncate text-primary-500 dark:text-primary-400">
              {placeholder}
            </span>
          )}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {companies && companies.length > 0 ? (
          companies.map((company) => (
            <SelectItem key={company.id} value={company.id}>
              <CompanyItem company={company} />
            </SelectItem>
          ))
        ) : (
          <div className="text-sm truncate px-2 py-[5px] text-primary-500 dark:text-primary-400">
            <span className="text-sm truncate">No companies found</span>
          </div>
        )}
      </SelectContent>
    </Select>
  )
}
